import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import PersonalInfoForm from './PersonalInfoForm'
import SecurityForm from './SecurityForm'
import AddressForm from './AddressForm'
import ServicesForm from './ServicesForm'
import AgendaForm from './AgendaForm'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaCheck, FaChevronLeft } from 'react-icons/fa'

import {
  Container,
  StepRow,
  StepContainer,
  StepCircle,
  StepLabel,
  StepLine,
  Circle,
  BackButton,
  Title,
  InfoText
} from './styles'

export default function AddProfessional () {
  const [loading, setLoading] = useState (true)
  const [accessLevels, setAccessLevels] = useState ([])
  const [cutileiServices, setCutileiServices] = useState ([])
  const [serviceCategories, setServiceCategories] = useState ([])
  const [professionals, setProfessionals] = useState ([])
  const [businessWorkingDays, setBusinessWorkingDays] = useState ([])
  const [businessTimeWindows, setBusinessTimeWindows] = useState ([])
  const [onlineTimeWindows, setOnlineTimeWindows] = useState ({})
  const [cutileiTimeWindows, setCutileiTimeWindows] = useState ({})
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const [professionalData, setProfessionalData] = useState ({
    name: '',
    nickname: '',
    cpf: '',
    rg: '',
    email: '',
    birth: '',
    gender: null,
    phone: '',
    access_level: null,
    password: '',
    password_confirmation: '',
    address: {
      route: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      postal_code: ''
    },
    accepts_cutilei: false,
    service_categories: [],
    services: [],
    working_days: []
  })

  const steps = (
    professionalData.access_level?.value.codename === 'service' ||
    !professionalData.access_level
  ) ? [
    {number: 0, label: 'Dados pessoais'},
    {number: 1, label: 'Segurança'},
    {number: 2, label: 'Endereço'},
    {number: 3, label: 'Serviços'},
    {number: 4, label: 'Agenda'}
  ] : [
    {number: 0, label: 'Dados pessoais'},
    {number: 1, label: 'Segurança'},
    {number: 2, label: 'Endereço'}
  ]

  const [currentStep, setCurrentStep] = useState (steps[0])

  useEffect (() => {
    getData ()
  }, [])

  useEffect (() => {
    if (businessWorkingDays.length > 0) {
      setProfessionalData (prev => ({
        ...prev,
        working_days: businessWorkingDays.map (day => {
          const work_start = TimeWindow.toShortTime (day.opening)
          const work_end = TimeWindow.toShortTime (day.closing)
          const online_start = TimeWindow.toShortTime (day.online_opening)
          const online_end = TimeWindow.toShortTime (day.online_closing)
          const cutilei_start = TimeWindow.toShortTime (day.cutilei_opening)
          const cutilei_end = TimeWindow.toShortTime (day.cutilei_closing)

          return {
            ...day,
            work_start: { label: work_start, value: work_start },
            work_end: { label: work_end, value: work_end },
            online_start: { label: online_start, value: online_start },
            online_end: { label: online_end, value: online_end },
            cutilei_start: { label: cutilei_start, value: cutilei_start },
            cutilei_end: { label: cutilei_end, value: cutilei_end },
            selected: true
          }
        })
      }))
    }
  }, [businessWorkingDays])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getData = async () => {
    try {
      const { data: accessLevels } = await cutileiApi.get (`/access_levels?business_id=${businessId}`)
      const { data: businessWorkingDays } = await cutileiApi.get (`/businesses/${businessId}/working_days`)
      const { data: serviceCategories } = await cutileiApi.get ('/service_categories', requestConfig)
      const { data: cutileiServices } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active&cutilei_services=true`,
        requestConfig
      )
      const { data: professionals } = await cutileiApi.get (
        `/businesses/${businessId}/professionals?access_level=service&status=active&deleted=false`,
        requestConfig
      )

      const timeWindows = {...businessWorkingDays.map (day => (
        TimeWindow.getTimeWindows (day.opening, day.closing)
      ))}

      const cutileiTimeWindows = {...businessWorkingDays.map (day => day.accepts_cutilei
        ? TimeWindow.getTimeWindows (day.cutilei_opening, day.cutilei_closing)
        : TimeWindow.getTimeWindows (day.opening, day.closing)
      )}

      const onlineTimeWindows = {...businessWorkingDays.map (day => day.accepts_online
        ? TimeWindow.getTimeWindows (day.online_opening, day.online_closing)
        : TimeWindow.getTimeWindows (day.opening, day.closing)
      )}

      setAccessLevels (accessLevels)
      setServiceCategories (serviceCategories)
      setCutileiServices (cutileiServices)
      setBusinessWorkingDays (businessWorkingDays)
      setBusinessTimeWindows (timeWindows)
      setOnlineTimeWindows (onlineTimeWindows)
      setCutileiTimeWindows (cutileiTimeWindows)
      setProfessionals (professionals)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  
  const prevStep = () => setCurrentStep (steps[currentStep.number - 1])
  const nextStep = () => setCurrentStep (steps[currentStep.number + 1])

  const handleFormSubmitted = formData => {
    setProfessionalData ({ ...professionalData, ...formData })
    nextStep ()
  }

  return (
    <Container>
      <BusinessNavbar/>
      <Title>Cadastrar profissional</Title>
      <StepRow>
        {steps.map ((step, index) => (
          <StepContainer key={index}>
            <StepCircle step={step} currentStep={currentStep}>
              {currentStep.number > step.number ? (
                <FaCheck color='#FFFFFF' size={14}/>
              ) : (
                <InfoText>{step.number + 1}</InfoText>
              )}
            </StepCircle>
            <StepLabel>{step.label}</StepLabel>
          </StepContainer>
        ))}
        <StepLine currentStep={currentStep.number} totalSteps={steps.length}/>
      </StepRow>
      {currentStep.number > 0 && (
        <BackButton onClick={prevStep}>
          <Circle>
            <FaChevronLeft color='#FFFFFF' size={14} style={{marginLeft: -2}}/>
          </Circle>
          <InfoText style={{ margin: 0, fontWeight: 500 }}>
            Voltar
          </InfoText>
        </BackButton>
      )}
      {loading ? <Loading/> : (
        <>
          {currentStep.number === 0 && (
            <PersonalInfoForm
              professionalData={professionalData}
              onFormSubmitted={handleFormSubmitted}
            />
          )}
          {currentStep.number === 1 && (
            <SecurityForm
              professionalData={professionalData}
              accessLevels={accessLevels}
              isTutorial={isTutorial}
              onFormSubmitted={handleFormSubmitted}
            />
          )}
          {currentStep.number === 2 && (
            <AddressForm
              professionalData={professionalData}
              onFormSubmitted={handleFormSubmitted}
            />
          )}
          {currentStep.number === 3 && (
            <ServicesForm
              professionalData={professionalData}
              serviceCategories={serviceCategories}
              cutileiServices={cutileiServices}
              professionals={professionals}
              onFormSubmitted={handleFormSubmitted}
            />
          )}
          {currentStep.number === 4 && (
            <AgendaForm
              professionalData={professionalData}
              businessWorkingDays={businessWorkingDays}
              businessTimeWindows={businessTimeWindows}
              onlineTimeWindows={onlineTimeWindows}
              cutileiTimeWindows={cutileiTimeWindows}
              professionals={professionals}
            />
          )}
        </>
      )}
    </Container>
  )
}
